import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const MAX_VISIBLE = 5

function getVisiblePages(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 0) return []
  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  let start = Math.max(1, currentPage - Math.floor(MAX_VISIBLE / 2))
  let end = Math.min(totalPages, start + MAX_VISIBLE - 1)
  start = Math.max(1, end - MAX_VISIBLE + 1)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pageNumbers = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  )

  if (totalPages <= 1) return null

  const showStartEllipsis = pageNumbers[0] > 1
  const showEndEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages

  return (
    <PaginationContainer>
      <PageButton
        type="button"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft size={15} strokeWidth={2.2} />
        Previous
      </PageButton>

      {showStartEllipsis && (
        <>
          <PageButton type="button" onClick={() => onPageChange(1)}>
            1
          </PageButton>
          {pageNumbers[0] > 2 && <Ellipsis aria-hidden>…</Ellipsis>}
        </>
      )}

      {pageNumbers.map((page) => (
        <PageButton
          key={page}
          type="button"
          $active={page === currentPage}
          onClick={() => onPageChange(page)}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </PageButton>
      ))}

      {showEndEllipsis && (
        <>
          {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
            <Ellipsis aria-hidden>…</Ellipsis>
          )}
          <PageButton type="button" onClick={() => onPageChange(totalPages)}>
            {totalPages}
          </PageButton>
        </>
      )}

      <PageButton
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label="Next page"
      >
        Next
        <ChevronRight size={15} strokeWidth={2.2} />
      </PageButton>
    </PaginationContainer>
  )
}

export default Pagination

const PaginationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
  padding-top: 4px;
`

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${(p) => (p.$active ? 'transparent' : 'rgba(23, 23, 23, 0.1)')};
  border-radius: 10px;
  padding: 0 12px;
  background: ${(p) => (p.$active ? gradient : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(23, 23, 23, 0.65)')};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? '0 4px 12px rgba(201, 77, 170, 0.22)' : 'none')};

  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: ${(p) => (p.$active ? '#fff' : '#c94daa')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const Ellipsis = styled.span`
  min-width: 24px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.35);
  user-select: none;
`
